import { Button, mergeClasses, tokens, Tree, TreeItem, TreeItemLayout } from '@fluentui/react-components';
import { TextBulletListSquareEdit20Regular } from '@fluentui/react-icons';
import { CSSProperties, FC, MouseEventHandler, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountAmountStyles } from '../../../hooks/useAccountAmountStyles';
import { IWhitePaperRow } from '../../../model/iWhitePaperRow';
import { formatNumberForDisplay, parseNumericValue } from '../../../utils/NumberUtils';
import { isInputRow } from '../../../utils/ReportUtils';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';
import LoadingSpinner from '../../common/LoadingSpinner';
import { DrillDownActionType } from '../ReportDrillDownActions';
import { AccountAmountWhitePaperRow } from '../ReportDrillDownUtils';
import { AccountAmountAdjustmentInfoTree } from './AccountAmountAdjustmentInfoTree';
import { AccountAmountBookInfoTree } from './AccountAmountBookInfoTree';
import { AdjustmentTypeSpacerCell } from './AdjustmentTypeSpacerCell';
import { IAccountAmountTabPanelProps } from './types';

function stopClickPropagation(e: React.MouseEvent): void {
    e.stopPropagation();
}

function reformatNumericText(value: string, numberFormatter: (v: number) => string): string {
    return numberFormatter(parseNumericValue(value, true));
}

const AccountAmountTabPanel: FC<IAccountAmountTabPanelProps> = (props: IAccountAmountTabPanelProps) => {
    const { t } = useTranslation();

    const enterAmountsDivStyles: CSSProperties = {
        marginLeft: '6px',
        marginRight: '4px',
        borderBottom: '1px solid',
        backgroundColor: tokens.colorNeutralBackground5,
        borderColor: tokens.colorBrandForegroundLink,
        borderRadius: '4px 4px 0px 0px',
        overflow: 'none',
    };

    const openEnterAmountsDialog: MouseEventHandler<HTMLButtonElement> = (event) => {
        props.updateDrillDownState &&
            props.updateDrillDownState({
                type: DrillDownActionType.EnterAmountsDialogOpened,
                enterAmountsDialogProps: {
                    buttonTrigger: event.target as HTMLElement,
                    case: props.report.case!,
                    jurisdiction: props.report.jurisdiction!,
                    period: props.report.year!.toString(),
                    entity: props.whitePaperReportColumn.entityCode ?? props.report.entityCode!,
                    adjustmentCode: '',
                    location: '',
                    isInputRow: isInputRow(props.selectedCell as IWhitePaperRow),
                },
            });
    };

    const accountAmountGridWrapperStyle: CSSProperties = useMemo(() => {
        return {
            display: props.isFetchingOrSavingData ? 'none' : 'flex',
            flexDirection: 'column',
            height: 'inherit',
        };
    }, [props.isFetchingOrSavingData]);
    const styles = useAccountAmountStyles();
    return (
        <div className={mergeClasses(styles.customTokens)} style={{ height: 'inherit' }}>
            {props.isDataLoaded && (
                <div style={enterAmountsDivStyles}>
                    <Button
                        appearance='secondary'
                        onClick={openEnterAmountsDialog}
                        aria-label={t('enterAmounts').toString()}
                        style={{ border: 'none', background: 'transparent' }}
                        icon={<TextBulletListSquareEdit20Regular color={tokens.colorBrandForeground2} />}
                        key='enterAmountsButton'
                    >
                        {t('enterAmounts').toString()}
                    </Button>
                </div>
            )}
            {props.isDataLoaded && (
                <div key='accountAmountGridWrapper' data-testid='accountAmountGrid' style={accountAmountGridWrapperStyle}>
                    <div role='rowgroup'>
                        <div role='row' className={mergeClasses(styles.headerRowContainer)}>
                            <div
                                role='columnheader'
                                className={mergeClasses(
                                    styles.accountColumnHeader,
                                    styles.treeHeader,
                                    styles.alignContentCenter,
                                    styles.fontSize
                                )}
                                data-automation-key='account'
                            >
                                {t('accountAdjustment')}
                            </div>
                            <div
                                role='columnheader'
                                className={mergeClasses(
                                    styles.adjustmentTypeColumn,
                                    styles.treeHeader,
                                    styles.alignContentCenter,
                                    styles.fontSize
                                )}
                                data-automation-key='adjustment-type'
                            >
                                {t('adjustmentTypeAbbreviated')}
                            </div>
                            <div
                                role='columnheader'
                                className={mergeClasses(
                                    styles.descriptionColumn,
                                    styles.treeHeader,
                                    styles.alignContentCenter,
                                    styles.fontSize
                                )}
                                data-automation-key='description'
                            >
                                {t('description')}
                            </div>
                            <div
                                role='columnheader'
                                className={mergeClasses(
                                    styles.reportAmountColumn,
                                    styles.treeHeader,
                                    styles.alignContentCenter,
                                    styles.fontSize
                                )}
                                data-automation-key='amount'
                            >
                                {t('reportAmount')}
                            </div>
                        </div>
                    </div>
                    {props.rows && props.rows.length ? (
                        <Tree
                            size='small'
                            navigationMode='treegrid'
                            className={styles.treeRoot}
                            openItems={props.openItems}
                            onOpenChange={props.onOpenChange}
                            key='tree'
                            collapseMotion={null}
                        >
                            {props.rows.map((whitePaperRow: AccountAmountWhitePaperRow, index: number) => {
                                return (
                                    <TreeItem
                                        key={whitePaperRow.uiKey!}
                                        itemType={'branch'}
                                        className={mergeClasses(
                                            styles.border,
                                            styles.rightMargin,
                                            index === 0 ? styles.topMargin : undefined
                                        )}
                                        value={whitePaperRow.uiKey!}
                                        role='row'
                                    >
                                        <TreeItemLayout
                                            main={{ className: mergeClasses(styles.flexFullWidth, styles.treeItemLayoutMain) }}
                                            className={mergeClasses(styles.alignItemsStart, styles.groupHeader)}
                                            expandIcon={{ className: mergeClasses(styles.treeChevron) }}
                                        >
                                            <div
                                                role='gridcell'
                                                className={mergeClasses(
                                                    styles.alignContentCenter,
                                                    styles.fontWeightSemibold,
                                                    styles.accountColumn,
                                                    styles.fontSize
                                                )}
                                                data-automation-key='account'
                                            >
                                                {whitePaperRow.account}
                                            </div>
                                            <AdjustmentTypeSpacerCell />
                                            <div
                                                role='gridcell'
                                                className={mergeClasses(
                                                    styles.descriptionColumn,
                                                    styles.alignContentCenter,
                                                    styles.fontWeightSemibold,
                                                    styles.fontSize
                                                )}
                                                data-automation-key='description'
                                            >
                                                <DefaultTooltipHostV9
                                                    appearance='inverted'
                                                    withArrow
                                                    positioning='before'
                                                    content={whitePaperRow.description ?? ''}
                                                >
                                                    {whitePaperRow.description}
                                                </DefaultTooltipHostV9>
                                            </div>
                                            <div
                                                role='gridcell'
                                                className={mergeClasses(
                                                    styles.reportAmountColumn,
                                                    styles.alignContentCenter,
                                                    styles.fontWeightBold,
                                                    styles.fontSize
                                                )}
                                                data-automation-key='amount'
                                            >
                                                {reformatNumericText(whitePaperRow.amount!, formatNumberForDisplay)}
                                            </div>
                                        </TreeItemLayout>
                                        {whitePaperRow.bookSummary ? (
                                            <div onClick={stopClickPropagation}>
                                                <AccountAmountBookInfoTree
                                                    row={whitePaperRow.bookSummary}
                                                    onJournalEntryClick={props.onJournalEntryClick}
                                                    journalEntryButtonDisplayState={props.journalEntryButtonDisplayState}
                                                    key={whitePaperRow.uiKey}
                                                    className={
                                                        !whitePaperRow.adjustmentSummaries && !whitePaperRow.bookSummary.bookEntries?.length
                                                            ? styles.treeRowLast
                                                            : styles.treeRow
                                                    }
                                                />
                                            </div>
                                        ) : null}
                                        {whitePaperRow.adjustmentSummaries ? (
                                            <Tree
                                                className={mergeClasses(styles.treeNoGap)}
                                                onClick={stopClickPropagation}
                                                collapseMotion={null}
                                            >
                                                <TreeItem itemType='leaf' onClick={stopClickPropagation}>
                                                    <TreeItemLayout
                                                        className={mergeClasses(
                                                            styles.alignItemsCenter,
                                                            styles.summaryRow,
                                                            styles.adjustmentsHeader
                                                        )}
                                                        main={{ className: mergeClasses(styles.treeItemLayoutMain) }}
                                                    >
                                                        <div role='heading' className={mergeClasses(styles.fontWeightSemibold)}>
                                                            {t('adjustments')}
                                                        </div>
                                                    </TreeItemLayout>
                                                </TreeItem>
                                                {whitePaperRow.adjustmentSummaries.map((adjustmentSummary, adjustmentIndex) => {
                                                    const isLastAdjustmentSummary =
                                                        adjustmentIndex === whitePaperRow.adjustmentSummaries!.length - 1;
                                                    const adjustmentChange = adjustmentSummary.uiKey
                                                        ? props.updatedChangeTrackingDataMap?.get(adjustmentSummary.uiKey)
                                                        : undefined;
                                                    const hasNoAdjustmentEntries = !adjustmentSummary.adjustmentEntries?.length;
                                                    const adjustmentClassName =
                                                        isLastAdjustmentSummary && hasNoAdjustmentEntries
                                                            ? styles.treeRowLast
                                                            : styles.treeRow;
                                                    return (
                                                        <div key={adjustmentSummary.uiKey} onClick={stopClickPropagation}>
                                                            <AccountAmountAdjustmentInfoTree
                                                                row={adjustmentSummary}
                                                                onEnterAmountsClick={props.onEnterAmountsClick!}
                                                                onJournalEntryClick={props.onJournalEntryClick}
                                                                journalEntryButtonDisplayState={props.journalEntryButtonDisplayState}
                                                                updatedChangeTrackingDataMap={props.updatedChangeTrackingDataMap}
                                                                onBlur={props.onAmountCellValueChange!}
                                                                rowChangeSummary={adjustmentChange}
                                                                className={adjustmentClassName}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </Tree>
                                        ) : null}
                                    </TreeItem>
                                );
                            })}
                        </Tree>
                    ) : (
                        <div className={mergeClasses(styles.noRecordsDisplay)}>{t('noRecordsToDisplay')}</div>
                    )}
                </div>
            )}
            {props.isFetchingOrSavingData && (
                <div className={styles.loadingSpinnerContainer}>
                    <LoadingSpinner id='loadingSpinnerAccountsAmount' label={t('loadingPleaseWait').toString()} />
                </div>
            )}
        </div>
    );
};

export default AccountAmountTabPanel;
